import React from "react";
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import type { AuditChecklistItem } from "@services/facilityCompliance";

const auditChecklistTableHeaderSx = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#FFF",
  fontFamily: "Rethink Sans, sans-serif",
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  borderBottom: "1px solid #0a6a80",
  padding: "10px 12px",
  backgroundColor: "#0a829d",
  verticalAlign: "top" as const,
  textAlign: "left" as const,
};

const auditChecklistTableCellSx = {
  fontSize: "13px",
  fontFamily: "Rethink Sans, sans-serif",
  color: "#262626",
  borderBottom: "1px solid #f0f0f0",
  padding: "10px 12px",
  verticalAlign: "top" as const,
  textAlign: "left" as const,
};

const ChecklistItemIcon = ({ completed }: { completed: boolean }) =>
  completed ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden
    >
      <path
        d="M15 6C15 6.55228 14.5523 7 14 7C13.4477 7 13 6.55228 13 6C13 5.44772 13.4477 5 14 5C14.5523 5 15 5.44772 15 6ZM12.5 2C9.42386 2 7 4.42386 7 7.5C7 7.89668 7.04001 8.29598 7.12154 8.67456C7.17968 8.94454 7.11378 9.17912 6.98018 9.31271L2.43934 13.8536C2.15804 14.1349 2 14.5164 2 14.9142V16.5C2 17.3284 2.67157 18 3.5 18H5.5C6.32843 18 7 17.3284 7 16.5V16H8C8.55228 16 9 15.5523 9 15V14H10C10.5523 14 11 13.5523 11 13V12.8199C11.4935 12.9536 12.0069 13 12.5 13C15.5761 13 18 10.5761 18 7.5C18 4.42386 15.5761 2 12.5 2ZM8 7.5C8 4.97614 9.97614 3 12.5 3C15.0239 3 17 4.97614 17 7.5C17 10.0239 15.0239 12 12.5 12C11.8408 12 11.2274 11.9047 10.7236 11.6528C10.5686 11.5753 10.3845 11.5836 10.2371 11.6747C10.0897 11.7658 10 11.9267 10 12.1V13H9C8.44772 13 8 13.4477 8 14V15H7C6.44772 15 6 15.4477 6 16V16.5C6 16.7761 5.77614 17 5.5 17H3.5C3.22386 17 3 16.7761 3 16.5V14.9142C3 14.7816 3.05268 14.6544 3.14645 14.5607L7.68729 10.0198C8.11942 9.58768 8.20942 8.97619 8.09913 8.46404C8.03354 8.15949 8 7.83105 8 7.5Z"
        fill="#065B70"
      />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8 4V8L10.67 10.67"
        stroke="#065b70"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="7.5" stroke="#065b70" fill="none" />
    </svg>
  );

export interface AuditChecklistTableProps {
  items: AuditChecklistItem[];
}

export const AuditChecklistTable: React.FC<AuditChecklistTableProps> = ({
  items,
}) => (
  <Box
    sx={{
      border: "1px solid #0a829d",
      borderRadius: "4px",
      overflow: "hidden",
    }}
  >
    <Box sx={{ overflowX: "auto" }}>
      <Table
        size="small"
        sx={{
          width: "100%",
          tableLayout: "auto",
          "& .MuiTableCell-root": { verticalAlign: "top" },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={auditChecklistTableHeaderSx}>
              CHECKLIST ITEM
            </TableCell>
            <TableCell
              sx={{
                ...auditChecklistTableHeaderSx,
                whiteSpace: "nowrap",
              }}
            >
              REQUIREMENT
            </TableCell>
            <TableCell sx={auditChecklistTableHeaderSx}>DESCRIPTION</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => {
            const isCompleted =
              item.status === "present" ||
              item.status === "completed" ||
              item.status === "done";
            const isRequired = item.required;

            return (
              <TableRow
                key={index}
                sx={{
                  "&:nth-of-type(even)": { backgroundColor: "#fafafa" },
                  "&:last-child td": { borderBottom: "none" },
                  "&:hover": { backgroundColor: "#f8fdfe" },
                }}
              >
                <TableCell
                  sx={{
                    ...auditChecklistTableCellSx,
                    fontWeight: 600,
                    minWidth: 160,
                    color: "#065b70",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        mt: "2px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ChecklistItemIcon completed={isCompleted} />
                    </Box>
                    <Box component="span">{item.name}</Box>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    ...auditChecklistTableCellSx,
                    whiteSpace: "nowrap",
                  }}
                >
                  {isRequired ? (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: "10px",
                        py: "3px",
                        borderRadius: "12px",
                        border: "1px solid #065b70",
                        backgroundColor: "#065b70",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: 500,
                        fontFamily: "Rethink Sans, sans-serif",
                      }}
                    >
                      Required
                    </Box>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: "10px",
                        py: "3px",
                        borderRadius: "12px",
                        border: "1px solid #d1d5db",
                        backgroundColor: "#f9fafb",
                        color: "#6b7280",
                        fontSize: "12px",
                        fontWeight: 500,
                        fontFamily: "Rethink Sans, sans-serif",
                      }}
                    >
                      Optional
                    </Box>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    ...auditChecklistTableCellSx,
                    minWidth: 220,
                    color: "#374151",
                  }}
                >
                  {item.description}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  </Box>
);
